import React, { useEffect, useRef } from 'react';
import { Pie } from '@antv/g2plot';
import type { ChartField } from '@/types/chat';
import { CHART_COLORS } from '@/common/constants';
import { toNum } from '@/utils';

interface PieChartProps {
  data: Record<string, unknown>[];
  dimensions?: ChartField[];
  metrics?: ChartField[];
}

export const PieChart: React.FC<PieChartProps> = ({ data, dimensions, metrics }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Pie | null>(null);

  const resolveFields = (d: Record<string, unknown>[]) => {
    const keys = d?.length ? Object.keys(d[0]) : [];
    return {
      colorField: dimensions?.[0]?.code ?? keys[0] ?? 'category',
      angleField: metrics?.[0]?.code ?? keys[1] ?? 'value',
      valueName: metrics?.[0]?.name ?? metrics?.[0]?.code ?? 'value',
      categoryName: dimensions?.[0]?.name ?? dimensions?.[0]?.code ?? 'category',
    };
  };

  const normalizeData = (d: Record<string, unknown>[], angleField: string) =>
    d.map((row) => ({ ...row, [angleField]: toNum(row[angleField]) }));

  useEffect(() => {
    if (!containerRef.current || !data?.length) return;

    const { colorField, angleField, valueName, categoryName } = resolveFields(data);

    chartRef.current = new Pie(containerRef.current, {
      data: normalizeData(data, angleField),
      angleField,
      colorField,
      autoFit: true,
      height: 260,
      radius: 0.8,
      innerRadius: 0.5,
      label: {
        type: 'inner',
        offset: '-30%',
        content: (datum: Record<string, unknown>) => `${(((datum.percent as number) ?? 0) * 100).toFixed(1)}%`,
        style: { fontSize: 12, textAlign: 'center' },
      },
      legend: {
        position: 'right',
        layout: 'vertical',
      },
      tooltip: {
        formatter: (datum: Record<string, unknown>) => ({
          name: String(datum[colorField] ?? categoryName),
          value: datum[angleField] as number,
        }),
      },
      color: CHART_COLORS,
      interactions: [{ type: 'element-active' }],
      statistic: {
        title: false,
        content: { style: { fontSize: '14px' }, content: valueName },
      },
    });

    chartRef.current.render();

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!chartRef.current || !data?.length) return;
    const { angleField } = resolveFields(data);
    chartRef.current.changeData(normalizeData(data, angleField));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return <div ref={containerRef} style={{ height: '100%', width: '100%' }} />;
};
